/**
 * In-memory Zustand store that retains chart filter selections (dates, components,
 * periods) and scenario simulation results across client-side page navigations.
 *
 * ## Lifecycle & Persistence
 * - State survives SPA route changes because the JS module context stays alive.
 * - State resets to defaults on a full page refresh — there is no persistence
 *   middleware (localStorage / sessionStorage) by design.
 * - On logout, `resetAllFilters()` is called to wipe all filter state AND the
 *   per-facility cache, ensuring no stale selections leak into the next session.
 *
 * ## Per-Facility Scoping
 * Filter selections are scoped to the active facility via an in-memory cache
 * (`facilityFilterCache`). When the user switches facilities or organizations:
 *   1. The current "active" filters are snapshotted into the cache, keyed by
 *      the **previous** facility ID.
 *   2. If the **next** facility has a cached snapshot it is restored; otherwise
 *      defaults are applied.
 * This is driven by `switchFacilityFilters(prev, next)`, called from the
 * organization store's `setActiveOrg` and `setActiveFacility` actions.
 *
 * ## Store Shape
 * Each page's filters are stored as explicit fields (not a generic map) so
 * TypeScript enforces which filters each page actually uses.
 * - Nullable fields (e.g. `performanceDateRange: null`) mean "use the page's
 *   own default"; non-null means the user has made a selection.
 *
 * ## Adding a New Filter
 * When a new page-level filter is introduced:
 *   1. Add the data field + setter to `ChartFilterState`.
 *   2. Add the field key to the `FacilityFilterSnapshot` Pick type.
 *   3. Add a default value in `getDefaultFilters()`.
 *   4. Map it in `extractSnapshot()`.
 *   5. Set the initial value in the `create(...)` block.
 */
import { create } from "zustand";
import type { ComponentKey, Granularity, ScenarioParams, ScenarioResponse } from "../data/types";
import { DEFAULT_SELECTED_COMPONENTS, type TelemetryStatusFilter } from "../components/system-usage/constants";

// ---------------------------------------------------------------------------
// Facility Filter Snapshot
// ---------------------------------------------------------------------------
// Subset of ChartFilterState that holds only *data* fields (no setters, no
// cache map). Used to save/restore a facility's filter selections.
// ---------------------------------------------------------------------------
type FacilityFilterSnapshot = Pick<ChartFilterState,
  | "selectedDate" | "selectedComponents" | "selectedTelemetryStatuses"
  | "kpiPeriod"
  | "performanceDateRange"
  | "aiInsightsStrategy" | "aiInsightsDateRange"
  | "resourcePerfDateRange" | "resourcePerfGranularity"
  | "energyDistComponent" | "energyDistResolution" | "energyDistDateRange"
  | "energyPatternComponent" | "energyPatternDateRange"
  | "resourceScopes"
  | "energyMetricsPeriod" | "resourceEnergyPeriod"
  | "scenarioActiveTab" | "scenarioSelectedDate" | "presetSelectedId"
  | "presetResults" | "presetLastParams" | "customParams" | "customResults" | "customLastParams"
>;

/**
 * Returns default filter values for a facility being visited for the first time.
 * Called as a function (not a constant) so `selectedDate` reflects the current
 * timestamp at the moment of the switch, not module-load time.
 */
const getDefaultFilters = (): FacilityFilterSnapshot => ({
  selectedDate: new Date().toISOString(),
  selectedComponents: DEFAULT_SELECTED_COMPONENTS,
  selectedTelemetryStatuses: [],
  kpiPeriod: "24h",
  performanceDateRange: null,
  aiInsightsStrategy: null,
  aiInsightsDateRange: null,
  resourcePerfDateRange: null,
  resourcePerfGranularity: "telemetry_interval",
  energyDistComponent: null,
  energyDistResolution: null,
  energyDistDateRange: null,
  energyPatternComponent: null,
  energyPatternDateRange: null,
  resourceScopes: {},
  energyMetricsPeriod: null,
  resourceEnergyPeriod: null,
  scenarioActiveTab: "preset",
  scenarioSelectedDate: null,
  presetSelectedId: "largerBattery",
  presetResults: null,
  presetLastParams: null,
  customParams: null,
  customResults: null,
  customLastParams: null,
});

/**
 * Copies the data fields out of the live store state into a plain snapshot
 * object suitable for caching. This deliberately omits setter functions
 * and the `facilityFilterCache` map itself.
 */
const extractSnapshot = (state: ChartFilterState): FacilityFilterSnapshot => ({
  selectedDate: state.selectedDate,
  selectedComponents: state.selectedComponents,
  selectedTelemetryStatuses: state.selectedTelemetryStatuses,
  kpiPeriod: state.kpiPeriod,
  performanceDateRange: state.performanceDateRange,
  aiInsightsStrategy: state.aiInsightsStrategy,
  aiInsightsDateRange: state.aiInsightsDateRange,
  resourcePerfDateRange: state.resourcePerfDateRange,
  resourcePerfGranularity: state.resourcePerfGranularity,
  energyDistComponent: state.energyDistComponent,
  energyDistResolution: state.energyDistResolution,
  energyDistDateRange: state.energyDistDateRange,
  energyPatternComponent: state.energyPatternComponent,
  energyPatternDateRange: state.energyPatternDateRange,
  resourceScopes: state.resourceScopes,
  energyMetricsPeriod: state.energyMetricsPeriod,
  resourceEnergyPeriod: state.resourceEnergyPeriod,
  scenarioActiveTab: state.scenarioActiveTab,
  scenarioSelectedDate: state.scenarioSelectedDate,
  presetSelectedId: state.presetSelectedId,
  presetResults: state.presetResults,
  presetLastParams: state.presetLastParams,
  customParams: state.customParams,
  customResults: state.customResults,
  customLastParams: state.customLastParams,
});

// ---------------------------------------------------------------------------
// State Interface
// ---------------------------------------------------------------------------
interface ChartFilterState {
  // ── System Component Comparison (Facility Overview) ─────────────────────
  // Date, component, and telemetry drill-down selections for the stacked-bar comparison chart.
  selectedDate: string | null;
  selectedComponents: ComponentKey[];
  selectedTelemetryStatuses: TelemetryStatusFilter[];
  setSelectedDate: (date: Date | null) => void;
  setSelectedComponents: (components: ComponentKey[]) => void;
  setSelectedTelemetryStatuses: (statuses: TelemetryStatusFilter[]) => void;
  /** Resets only the System Component Comparison filters (date + components + telemetry status). */
  resetFilters: () => void;
  /** Resets every filter across all pages AND clears the per-facility cache.
   *  Called on logout to prevent stale state from leaking into new sessions. */
  resetAllFilters: () => void;

  // ── Realtime Performance Metrics (Facility Overview) ────────────────────
  // Period toggle for the KPI cards (e.g. "24h", "7d", "30d").
  kpiPeriod: string;
  setKpiPeriod: (period: string) => void;

  // ── Performance Analysis ────────────────────────────────────────────────
  // ISO date-range strings sent to the performance-analysis API.
  // `from` = start-of-day, `to` = end-of-day in local-to-UTC conversion.
  performanceDateRange: { from: string; to: string } | null;
  setPerformanceDateRange: (range: { from: string; to: string }) => void;

  // ── Advanced Energy Analysis (AI Insights) ──────────────────────────────
  // Strategy mode (e.g. "cost_savings", "peak_shaving") and date window.
  // Dates stored via toISOStartOfDay / toISOEndOfDay (UTC with local date
  // components) — when restoring for the picker, extract the date-only
  // portion (`.slice(0, 10)`) to avoid timezone-induced off-by-one errors.
  aiInsightsStrategy: string | null;
  setAiInsightsStrategy: (strategy: string) => void;
  aiInsightsDateRange: { start: string; end: string } | null;
  setAiInsightsDateRange: (range: { start: string; end: string }) => void;

  // ── Resource Performance & Breakdown ────────────────────────────────────
  // Week-level date range for the resource breakdown charts.
  resourcePerfDateRange: { from: string; to: string } | null;
  setResourcePerfDateRange: (range: { from: string; to: string }) => void;
  resourcePerfGranularity: Granularity;
  setResourcePerfGranularity: (granularity: Granularity) => void;

  // ── Energy Usage Patterns (EnergyDistribution) ──────────────────────────
  // Component filter, resolution (hourly/daily), and date range for the
  // box-plot distribution chart.
  energyDistComponent: ComponentKey | null;
  setEnergyDistComponent: (component: ComponentKey) => void;
  energyDistResolution: string | null;
  setEnergyDistResolution: (resolution: string) => void;
  energyDistDateRange: { from: string; to: string } | null;
  setEnergyDistDateRange: (range: { from: string; to: string }) => void;

  // ── Energy Pattern Heatmap ──────────────────────────────────────────────
  // Component and date range for the heatmap (Nivo).
  energyPatternComponent: ComponentKey | null;
  setEnergyPatternComponent: (component: ComponentKey) => void;
  energyPatternDateRange: { from: string; to: string } | null;
  setEnergyPatternDateRange: (range: { from: string; to: string }) => void;

  // ── Resource Card Scope Selections ──────────────────────────────────────
  // Maps resourceType → selected scope id (e.g. individual inverter).
  resourceScopes: Record<string, string>;
  setResourceScope: (resourceType: string, scopeId: string) => void;

  // ── Energy Analysis Parent Periods ──────────────────────────────────────
  // Top-level period selectors on the Energy Analysis page.
  energyMetricsPeriod: string | null;
  setEnergyMetricsPeriod: (period: string) => void;
  resourceEnergyPeriod: string | null;
  setResourceEnergyPeriod: (period: string) => void;

  // ── Scenario Simulator ──────────────────────────────────────────────────
  // Active tab, selected date, chosen preset, and cached results for both
  // preset and custom simulation modes.
  scenarioActiveTab: "preset" | "custom";
  setScenarioActiveTab: (tab: "preset" | "custom") => void;
  scenarioSelectedDate: string | null;
  setScenarioSelectedDate: (date: string) => void;
  presetSelectedId: string;
  setPresetSelectedId: (id: string) => void;
  presetResults: ScenarioResponse | null;
  setPresetResults: (results: ScenarioResponse | null) => void;
  presetLastParams: ScenarioParams | null;
  setPresetLastParams: (params: ScenarioParams | null) => void;
  customParams: ScenarioParams | null;
  setCustomParams: (params: ScenarioParams) => void;
  customResults: ScenarioResponse | null;
  setCustomResults: (results: ScenarioResponse | null) => void;
  customLastParams: ScenarioParams | null;
  setCustomLastParams: (params: ScenarioParams | null) => void;

  // ── Per-Facility Filter Cache ───────────────────────────────────────────
  // In-memory map of facilityId → snapshot. Populated/consumed by
  // `switchFacilityFilters` when the org store changes the active facility.
  facilityFilterCache: Record<number, FacilityFilterSnapshot>;
  /**
   * Atomically saves the current filters under `prevFacilityId` and restores
   * (or defaults) the filters for `nextFacilityId`.
   *
   * Called by `useOrganizationStore.setActiveOrg` / `setActiveFacility`.
   * Skips the save step when `prevFacilityId` is 0 (initial app load).
   */
  switchFacilityFilters: (prevFacilityId: number, nextFacilityId: number) => void;
}

// ---------------------------------------------------------------------------
// Store Implementation
// ---------------------------------------------------------------------------
export const useChartFilterStore = create<ChartFilterState>()((set) => ({
  // ── System Component Comparison ─────────────────────────────────────────
  selectedDate: new Date().toISOString(),
  selectedComponents: DEFAULT_SELECTED_COMPONENTS,
  selectedTelemetryStatuses: [],
  setSelectedDate: (date) =>
    set({ selectedDate: date ? date.toISOString() : null }),
  setSelectedComponents: (components) =>
    set({ selectedComponents: components }),
  setSelectedTelemetryStatuses: (statuses) =>
    set({ selectedTelemetryStatuses: statuses }),
  resetFilters: () =>
    set({
      selectedDate: new Date().toISOString(),
      selectedComponents: DEFAULT_SELECTED_COMPONENTS,
      selectedTelemetryStatuses: [],
    }),
  resetAllFilters: () =>
    set({ ...getDefaultFilters(), facilityFilterCache: {} }),

  // ── Realtime Performance Metrics ────────────────────────────────────────
  kpiPeriod: "24h",
  setKpiPeriod: (period) => set({ kpiPeriod: period }),

  // ── Performance Analysis ────────────────────────────────────────────────
  performanceDateRange: null,
  setPerformanceDateRange: (range) => set({ performanceDateRange: range }),

  // ── Advanced Energy Analysis ────────────────────────────────────────────
  aiInsightsStrategy: null,
  setAiInsightsStrategy: (strategy) => set({ aiInsightsStrategy: strategy }),
  aiInsightsDateRange: null,
  setAiInsightsDateRange: (range) => set({ aiInsightsDateRange: range }),

  // ── Resource Performance & Breakdown ────────────────────────────────────
  resourcePerfDateRange: null,
  setResourcePerfDateRange: (range) => set({ resourcePerfDateRange: range }),
  resourcePerfGranularity: "telemetry_interval",
  setResourcePerfGranularity: (granularity) => set({ resourcePerfGranularity: granularity }),

  // ── Energy Usage Patterns ───────────────────────────────────────────────
  energyDistComponent: null,
  setEnergyDistComponent: (component) => set({ energyDistComponent: component }),
  energyDistResolution: null,
  setEnergyDistResolution: (resolution) => set({ energyDistResolution: resolution }),
  energyDistDateRange: null,
  setEnergyDistDateRange: (range) => set({ energyDistDateRange: range }),

  // ── Energy Pattern Heatmap ──────────────────────────────────────────────
  energyPatternComponent: null,
  setEnergyPatternComponent: (component) => set({ energyPatternComponent: component }),
  energyPatternDateRange: null,
  setEnergyPatternDateRange: (range) => set({ energyPatternDateRange: range }),

  // ── Resource Card Scopes ────────────────────────────────────────────────
  resourceScopes: {},
  setResourceScope: (resourceType, scopeId) =>
    set((state) => ({ resourceScopes: { ...state.resourceScopes, [resourceType]: scopeId } })),

  // ── Energy Analysis Parent Periods ──────────────────────────────────────
  energyMetricsPeriod: null,
  setEnergyMetricsPeriod: (period) => set({ energyMetricsPeriod: period }),
  resourceEnergyPeriod: null,
  setResourceEnergyPeriod: (period) => set({ resourceEnergyPeriod: period }),

  // ── Scenario Simulator ──────────────────────────────────────────────────
  scenarioActiveTab: "preset",
  setScenarioActiveTab: (tab) => set({ scenarioActiveTab: tab }),
  scenarioSelectedDate: null,
  setScenarioSelectedDate: (date) => set({ scenarioSelectedDate: date }),
  presetSelectedId: "largerBattery",
  setPresetSelectedId: (id) => set({ presetSelectedId: id }),
  presetResults: null,
  setPresetResults: (results) => set({ presetResults: results }),
  presetLastParams: null,
  setPresetLastParams: (params) => set({ presetLastParams: params }),
  customParams: null,
  setCustomParams: (params) => set({ customParams: params }),
  customResults: null,
  setCustomResults: (results) => set({ customResults: results }),
  customLastParams: null,
  setCustomLastParams: (params) => set({ customLastParams: params }),

  // ── Per-Facility Filter Cache ───────────────────────────────────────────
  facilityFilterCache: {},
  switchFacilityFilters: (prevFacilityId, nextFacilityId) =>
    set((state) => {
      // 1. Snapshot current active filters under the outgoing facility.
      const updatedCache = { ...state.facilityFilterCache };
      if (prevFacilityId) {
        updatedCache[prevFacilityId] = extractSnapshot(state);
      }
      // 2. Restore cached snapshot for the incoming facility, or fall
      //    back to fresh defaults if this is the first visit.
      const cached = updatedCache[nextFacilityId];
      return { ...(cached ?? getDefaultFilters()), facilityFilterCache: updatedCache };
    }),
}));
